// src/pages/shop/ShopCategories.jsx
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { categories as categoriesApi, upload as uploadApi } from '../../api';
import { useToast } from '../../hooks/useToast';
import { useDeletePin } from '../../hooks/useDeletePin';
import BottomNav from '../../components/BottomNav';
import Icon from '../../components/Icon';
import { SHOP_NAV, useShopNav } from './ShopHome';

export default function ShopCategories() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { show, ToastView } = useToast();
  const { confirm: confirmDelete, PinModal } = useDeletePin();
  const navItems = useShopNav();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try { setCats(await categoriesApi.list()); }
    catch (e) { show(e.message, 'error'); }
    setLoading(false);
  };

  const remove = async (cat) => {
    const subCount = cats.filter(c => c.parentId === cat.id).length;
    const msg = subCount
      ? t('categories.confirmDeleteWithSubs', { name: cat.name, count: subCount })
      : t('categories.confirmDelete', { name: cat.name });
    const ok = await confirmDelete({ title: t('categories.deleteTitle', 'Удалить категорию?'), message: msg });
    if (!ok) return;
    try { await categoriesApi.remove(cat.id); show(t('categories.deleted')); load(); }
    catch (e) { show(e.message, 'error'); }
  };

  const rootCats = cats.filter(c => !c.parentId);

  if (loading) return <div className="loader"><div className="spinner" /></div>;

  return (
    <div className="page">
      {ToastView}
      {PinModal}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          <Icon name="chevronLeft" size={18} />
        </button>
        <div style={{ flex: 1 }}>
          <p className="page-subtitle">{t('categories.subtitleCount', { count: cats.length })}</p>
          <h1 className="page-title">{t('categories.title')}</h1>
        </div>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => setEditing({ parentId: null })}
          style={{ width: 'auto', padding: '8px 14px' }}
        >
          <Icon name="plus" size={14} stroke={2.5} />
          {t('categories.newButton')}
        </button>
      </div>

      {rootCats.length === 0 ? (
        <div className="empty-state">
          <div className="icon"><Icon name="folder" size={28} stroke={1.5} /></div>
          <p>{t('categories.noCategories')}.<br/>{t('categories.noCategoriesHint')}</p>
        </div>
      ) : (
        <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rootCats.map(rc => {
            const subs = cats.filter(c => c.parentId === rc.id);
            return (
              <div key={rc.id} className="card">
                <CatRow cat={rc} onEdit={() => setEditing(rc)} onDelete={() => remove(rc)} isRoot />

                {subs.length > 0 && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {subs.map(s => (
                      <CatRow key={s.id} cat={s} onEdit={() => setEditing(s)} onDelete={() => remove(s)} indented />
                    ))}
                  </div>
                )}

                <button
                  onClick={() => setEditing({ parentId: rc.id })}
                  style={{
                    marginTop: 10, padding: '8px',
                    background: 'var(--bg-subtle)', border: 'none', borderRadius: 'var(--r-sm)',
                    color: 'var(--fg-muted)', fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', width: '100%',
                    fontFamily: 'var(--font-display)',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  }}
                >
                  <Icon name="plus" size={14} />
                  {t('categories.addSubcategory')}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <CategoryEditor
          editing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); show(t('common.success')); }}
          onError={(m) => show(m, 'error')}
        />
      )}

      <BottomNav items={navItems} />
    </div>
  );
}

function CatRow({ cat, onEdit, onDelete, isRoot, indented }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      paddingLeft: indented ? 8 : 0,
    }}>
      {cat.photo ? (
        <img src={cat.photo} alt=""
          style={{
            width: isRoot ? 44 : 36, height: isRoot ? 44 : 36,
            borderRadius: 'var(--r-sm)', objectFit: 'cover',
            flexShrink: 0,
          }} />
      ) : (
        <div style={{
          width: isRoot ? 44 : 36, height: isRoot ? 44 : 36,
          borderRadius: 'var(--r-sm)',
          background: isRoot ? 'var(--brand-primary-soft)' : 'var(--bg-subtle)',
          color: isRoot ? 'var(--brand-primary)' : 'var(--fg-faint)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon name={isRoot ? 'folder' : 'folderOpen'} size={isRoot ? 20 : 16} />
        </div>
      )}
      <p style={{
        flex: 1, fontSize: isRoot ? 15 : 13, fontWeight: isRoot ? 700 : 600,
        color: isRoot ? 'var(--fg)' : 'var(--fg-muted)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {cat.name}
      </p>
      <button onClick={onEdit} style={smallIcon}><Icon name="edit" size={14} /></button>
      <button onClick={onDelete} style={smallIcon}><Icon name="trash" size={14} /></button>
    </div>
  );
}

const smallIcon = {
  width: 28, height: 28, background: 'var(--bg-subtle)',
  border: 'none', borderRadius: 'var(--r-xs)', cursor: 'pointer',
  color: 'var(--fg-muted)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  flexShrink: 0,
};

function CategoryEditor({ editing, onClose, onSaved, onError }) {
  const { t } = useTranslation();
  const nameRef = useRef();
  const fileRef = useRef();
  const [photo, setPhoto] = useState(editing?.photo || null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const isEdit = !!editing.id;
  const isSubcategory = !!editing.parentId;

  const pickPhoto = () => fileRef.current?.click();

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const { urls } = await uploadApi.photos(files);
      if (urls?.length) setPhoto(urls[0]);
    } catch (err) { onError(err.message); }
    setUploading(false);
    e.target.value = '';
  };

  const save = async () => {
    const name = nameRef.current?.value?.trim();
    if (!name) return onError(t('categories.errorNameRequired'));
    setSaving(true);
    try {
      if (isEdit) {
        await categoriesApi.update(editing.id, { name, photo });
      } else {
        await categoriesApi.create({ name, parentId: editing.parentId || null, photo });
      }
      onSaved();
    } catch (e) { onError(e.message); }
    setSaving(false);
  };

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-sheet">
        <div className="modal-header">
          <p className="modal-title">
            {isEdit ? t('categories.edit') : isSubcategory ? t('categories.newSubcategory') : t('categories.newCategory')}
          </p>
          <button className="modal-close" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>

        <input ref={fileRef} type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />

        <div onClick={!uploading ? pickPhoto : undefined}
          style={{
            width: '100%', aspectRatio: '4/3',
            background: 'var(--bg-subtle)', border: '2px dashed var(--border)',
            borderRadius: 'var(--r-md)', marginBottom: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: uploading ? 'default' : 'pointer',
            overflow: 'hidden', position: 'relative',
          }}>
          {uploading ? (
            <div className="spinner" />
          ) : photo ? (
            <>
              <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <button
                onClick={(e) => { e.stopPropagation(); setPhoto(null); }}
                style={{
                  position: 'absolute', top: 8, right: 8,
                  background: 'rgba(0,0,0,0.6)', color: '#fff',
                  border: 'none', borderRadius: 999,
                  width: 28, height: 28, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                <Icon name="x" size={14} />
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', color: 'var(--fg-faint)' }}>
              <Icon name="image" size={28} stroke={1.5} style={{ margin: '0 auto 6px' }} />
              <div style={{ fontSize: 13 }}>{t('products.addPhoto')}</div>
            </div>
          )}
        </div>
        <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginBottom: 14, marginTop: -4 }}>
          {t('categories.photoHint')}
        </p>

        <div className="input-group">
          <label>{t('categories.name')}</label>
          <input
            ref={nameRef}
            className="input"
            defaultValue={editing.name || ''}
            placeholder={isSubcategory ? t('categories.namePlaceholderSub') : t('categories.namePlaceholder')}
            autoFocus
          />
        </div>

        <button className="btn btn-primary btn-lg" onClick={save} disabled={saving}>
          {saving ? t('categories.saving') : (isEdit ? t('categories.save') : t('categories.create'))}
        </button>
      </div>
    </div>
  );
}
